<template>

  <div class="ob-content">
    <ob-filters @close="refresh()">
      <log-filters :filters="filters" @change="filters = $event" @variables="leafVariableIds = $event"></log-filters>

      <template v-slot:controls="{ options }">
        <v-btn :depressed="!options.mobile" color="primary" :disabled="loading" @click="refresh()">
          <responsive-icon :label="$t('shared.refresh')" icon="redo-alt" />
        </v-btn>
      </template>

      <template v-slot:actions>
        <v-list-item @click="deviceStatistics()">
          <v-list-item-title>
            <font-awesome-icon icon="chart-bar" fixed-width />
            {{ $t('device-statistics') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item @click="print()">
          <v-list-item-title>
            <font-awesome-icon icon="print" fixed-width />
            {{ $t('shared.print') }}
          </v-list-item-title>
        </v-list-item>
      </template>
    </ob-filters>

    <loader :condition="!loading">
      <v-card v-if="selectedVariablesData.length">
        <history
          ref="history"
          v-if="selectedVariablesData.length"
          :now="now"
          :time-from="filters.time.from"
          :time-to="filters.time.to"
          :data="selectedVariablesData"
          :user-data="userData"
          @timeInterval="changeTimeInterval({ from: $event.timeFrom, to: $event.timeTo })"
        />
      </v-card>
    </loader>

    <no-data v-if="!selectedVariablesData.length">{{ $t('logs.no-assets-selected') }}</no-data>
  </div>

</template>

<script>
import History from './history'
import { loadAsset11Logs, loadAsset12Logs } from './history-generator'
import { getAssociatedAssetIds } from './associated-devices.es6'
import DateRangePicker from '../shared/components/date-range-picker.vue'
import UrlService from '../../services/url.service.es6'
import '../../assets/logs-print.css'
import Loader from '../shared/loader'
import NoData from '../shared/no-data'
import AssetsTree from '../assets/components/assets-tree'
import VariablesTree from '../assets/components/variables-tree'
import LogFilters from './log-filters'
import ObFilters from '../shared/components/filters'
import ResponsiveIcon from '../shared/components/responsive-icon'

const generatedData = false
const variableSeparator = '-'

const sec = 1000
const min = 60 * sec
const nowOffsetPc = 5 // now vertical line is x % from right window border
const defaultTimeIntervalWidth = 60 * min
const realTimeRefreshInterval = 5 * sec

export default {
  data () {
    return {
      data: {},
      now: null,
      filters: {
        assetIds: [],
        variableIds: [],
        time: this.defaultTime
      },
      leafVariableIds: [],
      selectedVariablesData: [],
      realTime: false,
      loading: false,
      userData: { // tweaks in presentation set by the user
        variables: {}
      }
    }
  },

  computed: {
    defaultTime () {
      const from = this.now - defaultTimeIntervalWidth * (1 - nowOffsetPc / 100)

      return {
        from: from,
        to: from + defaultTimeIntervalWidth
      }
    },

    variableIds () {
      return this.filters.variableIds
    }
  },

  watch: {
    variableIds: {
      handler () {
        this.calculateSelectedVariablesData()
      },
      deep: true
    },

    realTime () {
      this.scheduleRealTimeRefresh()
    }
  },

  async mounted () {
    const parseDate = value => new Date(value).getTime() || new Date(parseInt(value)).getTime() || null
    const parseArray = value => value.split(',').filter(x => !!x)

    this.now = await this.getServerTime()

    this.filters.assetIds = parseArray(this.$route.query.assets || '')
    this.filters.variableIds = parseArray(this.$route.query.variables || '')

    if (this.$route.params.associatedAssets) {
      const tree = await this.$store.dispatch('getStaticItems', { model: { name: 'assets', tree: true } })
      this.filters.assetIds = this.filters.assetIds.map(asset => [ asset, ...getAssociatedAssetIds(asset, tree.clone().nodes) ]).flat()
    }

    this.changeTimeInterval({
      from: parseDate(this.$route.query.from),
      to: parseDate(this.$route.query.to)
    })

    this.load()
  },

  methods: {
    refresh () {
      this.saveState()
      this.load()
    },

    saveState () {
      UrlService.patchQuery({
        from: this.filters.time.from,
        to: this.filters.time.to,
        assets: this.filters.assetIds.join(','),
        variables: this.filters.variableIds.join(',')
      })
    },

    async load () {
      if (!this.filters.assetIds.length)
        return

      try {
        this.loading = true

        if (generatedData) {
          await this.loadDataGenerated()
        } else {
          await this.loadData()
        }

        this.data = this.$store.state.assetLogs
        this.calculateSelectedVariablesData()

      } catch (error) {
        console.log('[logs/load] Failed to load data', error)

      } finally {
        this.loading = false
      }
    },

    async loadData () {
      this.$store.commit('clearLogs')

      await Promise.all(
        this.filters.assetIds.map(assetId => this.$store.dispatch('getLogs', {
          assetId: assetId,
          timeFrom: this.filters.time.from,
          timeTo: this.filters.time.to
        }))
      )
    },

    loadDataGenerated () {
      const { timeFrom, timeTo } = this
      return Promise.all([
        loadAsset11Logs(this.$store.commit, timeFrom, timeTo),
        loadAsset12Logs(this.$store.commit, timeFrom, timeTo)
      ])
    },

    calculateSelectedVariablesData () {
      this.selectedVariablesData = Object.entries(this.data)
        .map(([id, asset]) => ({ id, ...asset }))
        .filter(asset => this.filters.assetIds.indexOf(asset.id) >= 0)
        .map(asset => {
          let variables = asset.variables ? Object.values(asset.variables) : []
          if (!generatedData) {
            variables = variables.filter(variable => this.leafVariableIds.includes(asset.deviceTyp + variableSeparator + variable.name))
          }
          return { ...asset, variables }
        })
        .sort((a, b) => Math.sign(this.filters.assetIds.indexOf(a.id) - this.filters.assetIds.indexOf(b.id))) // sorted in selected order
    },

    async changeTimeInterval ({ from, to }) {
      // show default interval
      if (!from)
        this.filters.time = this.defaultTime

      // show exact interval
      else if (from && to)
        this.filters.time = { from, to }

        // show center time interval
      else if (!to)
        this.filters.time = { from: from - defaultTimeIntervalWidth / 2, to: from + defaultTimeIntervalWidth / 2 }

      this.saveState()
      await this.load()
    },

    scheduleRealTimeRefresh () {
      window.setTimeout(async () => {
        if (this.realTime) {
          await this.performRealTimeRefresh()
          this.scheduleRealTimeRefresh()
        }
      }, realTimeRefreshInterval)
    },

    async performRealTimeRefresh () {
      const time = await this.getServerTime()
      const { timeFrom, timeTo, now } = this
      const diff = time - now
      const newTimeFrom = timeFrom + diff
      const newTimeTo = timeTo + diff
      this.now = time
      await this.changeTimeInterval({ from: newTimeFrom, to: newTimeTo })
    },

    async getServerTime () {
      try {
        return await this.$store.dispatch('getServerTime')
      } catch (error) {
        console.error('Failed to fetch server time:', error.message)
        return new Date().valueOf()
      }
    },

    print () {
      window.print()
      return false
    },

    deviceStatistics () {
      const { assetIds, variableIds, timeRange } = this
      this.$router.push({
        name: 'reports/device-statistics',
        params: { assetIds, variableIds, timeRange }
      })
    }
  },

  components: {
    ResponsiveIcon,
    ObFilters,
    LogFilters,
    AssetsTree,
    VariablesTree,
    Loader,
    NoData,
    History,
    DateRangePicker
  }
}
</script>

<style scoped lang="scss">


</style>
